import { useEffect, useState } from "react";
import { Check, AlertTriangle, ArrowDown } from "lucide-react";

// Section 1 — "What is Meshly?" The enterprise AI stack. Before Meshly there is
// an empty slot and a set of problems; with Meshly the layer inserts into the
// slot and each problem resolves into its mirror.
const PAIRS = [
  { bad: "Conflicting answers", good: "One agreed answer" },
  { bad: "No lineage", good: "Full lineage" },
  { bad: "Inconsistent meaning", good: "Consistent meaning" },
  { bad: "Can't explain the decision", good: "Every decision explained" },
];
const APP_LOGOS = ["salesforce", "stripe", "quickbooks"];

const Arrow = () => <ArrowDown className="my-1 h-4 w-4 text-foreground/25" />;

export default function TrustLayerStack() {
  const [on, setOn] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => setOn((o) => !o), on ? 5200 : 7800);
    return () => window.clearTimeout(id);
  }, [on, paused]);

  return (
    <div className="rounded-2xl border border-foreground/10 bg-background p-8 md:p-10">
      <div className="mb-8 flex justify-center">
        <div className="flex gap-1 rounded-xl border border-border bg-secondary p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setOn(false)}
            className={`rounded-lg px-5 py-2 font-mono-tech text-xs font-semibold uppercase tracking-wider transition-colors ${
              !on ? "bg-background text-red-600 shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Before Meshly
          </button>
          <button
            type="button"
            onClick={() => setOn(true)}
            className={`rounded-lg px-5 py-2 font-mono-tech text-xs font-semibold uppercase tracking-wider transition-colors ${
              on ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            With Meshly
          </button>
        </div>
      </div>

      <div
        className="grid items-center gap-8 md:grid-cols-[1fr_auto]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* the stack */}
        <div className="mx-auto flex w-full max-w-sm flex-col items-center">
          <div className="w-full rounded-xl border border-border bg-background px-5 py-3.5 text-center">
            <p className="text-sm font-semibold text-foreground">Business decisions</p>
          </div>
          <Arrow />
          <div className="w-full rounded-xl border border-border bg-background px-5 py-3.5 text-center">
            <p className="text-sm font-semibold text-foreground">AI agents</p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-red-500 animate-vh-pulse"}`} />
              executing workflows, taking autonomous action
            </p>
          </div>
          <Arrow />
          {/* Business applications — lifted prominence (orients the viewer) */}
          <div className="w-full rounded-xl border-2 border-foreground/20 bg-secondary/50 px-5 py-4 text-center shadow-sm">
            <p className="text-sm font-semibold text-foreground">Business applications</p>
            <div className="mt-2 flex items-center justify-center gap-2.5">
              {APP_LOGOS.map((l) => (
                <img key={l} src={`/logos/${l}.svg`} alt={l} className="h-5 w-5" />
              ))}
              <span className="font-mono-tech text-[11px] uppercase tracking-wider text-muted-foreground">CRM · ERP · Billing · CLM</span>
            </div>
          </div>
          <Arrow />
          {/* Meshly — empty slot before, inserted after */}
          {on ? (
            <div
              key="meshly-on"
              className="vh-insert w-full rounded-xl border-2 border-primary bg-primary/[0.07] px-5 py-5 text-center shadow-[0_0_0_5px_hsl(238_67%_52%/0.10)]"
            >
              <p className="text-xl font-bold tracking-tight text-primary">Meshly</p>
              <p className="mt-1 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">the trust harness</p>
            </div>
          ) : (
            <div className="flex w-full items-center justify-center rounded-xl border-2 border-dashed border-foreground/25 px-5 py-5 text-center">
              <p className="font-mono-tech text-[11px] uppercase tracking-wider text-muted-foreground/70">trust harness missing</p>
            </div>
          )}
          <Arrow />
          <div className="w-full rounded-xl border border-border bg-background px-5 py-3.5 text-center">
            <p className="text-sm font-semibold text-foreground">Data sources</p>
          </div>
        </div>

        {/* mirrored before / after */}
        <div className="flex min-h-[210px] w-full flex-col justify-center gap-2.5 md:w-60">
          {PAIRS.map((p, i) => (
            <span
              key={i}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                on ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-600"
              }`}
            >
              {on ? <Check className="h-4 w-4 shrink-0" strokeWidth={3} /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
              {on ? p.good : p.bad}
            </span>
          ))}
          <span className="mt-1 text-center font-mono-tech text-[11px] font-medium uppercase tracking-wider text-muted-foreground md:text-left">
            {on ? "with Meshly" : "without a trust harness"}
          </span>
        </div>
      </div>
    </div>
  );
}
